// Live data layer for Team Management. One RPC (get_hierarchy_tree) returns
// the viewer's whole scoped payload; buildHierarchyTree turns it into the
// OrgNode tree the page renders. Flag writes go through set_org_flag; the
// client click-through loads real scenarios via get_client_scenarios.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { rowToScenario } from '@/tax/scenarioStore';
import type { TaxScenario } from '@/tax/types';
import type { OrgNode, ClientRef, AuditEntry, FlagKey } from './hierarchyTree';

// ---------------------------------------------------------------------------
// RPC payload shapes (mirror get_hierarchy_tree in the migration)
// ---------------------------------------------------------------------------
export interface HierarchyPayload {
  root_org_id: string;
  is_platform_view: boolean;
  viewer_granted: { alts: boolean; fia: boolean };
  orgs: { id: string; name: string; org_type: string; parent_org_id: string | null; owner_name: string | null; staff_owned?: boolean }[];
  members: { user_id: string; org_id: string; full_name: string | null; email: string | null; status: string | null; org_role: string | null; client_count: number; has_client_book?: boolean }[];
  clients: { client_id: string; agent_user_id: string; name: string; updated_at: string }[];
  flags: { org_id: string; flag_name: string; enabled: boolean }[];
  user_flags: { user_id: string; flag_name: string; enabled: boolean }[];
  audit: { actor_name: string | null; action: string; target_type: string | null; details: Record<string, unknown> | null; created_at: string }[];
}

export interface HierarchyData {
  root: OrgNode;
  isPlatformView: boolean;
  granted: Record<FlagKey, boolean>;
  overrides: Record<string, boolean>;
  audit: AuditEntry[];
}

// ---------------------------------------------------------------------------
// Relative time — "2 hours ago" style, matching the design's lastEdit copy
// ---------------------------------------------------------------------------
export function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.floor((now.getTime() - then) / 60_000));
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? 'yesterday' : `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return weeks === 1 ? 'last week' : `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  return months <= 1 ? '1 month ago' : `${months} months ago`;
}

// ---------------------------------------------------------------------------
// Payload → OrgNode tree (pure; unit-tested)
// ---------------------------------------------------------------------------
const AUDIT_VERBS: Record<string, { verb: string; kind: AuditEntry['kind'] }> = {
  'org.flag_changed':      { verb: 'changed a feature flag for', kind: 'flag' },
  'impersonation.started': { verb: 'viewed as', kind: 'role' },
  'scenario.created':      { verb: 'created a scenario for', kind: 'add' },
  'scenario.updated':      { verb: 'updated a scenario for', kind: 'dup' },
  'scenario.deleted':      { verb: 'deleted a scenario for', kind: 'status' },
  'client.duplicated':     { verb: 'duplicated a client for', kind: 'dup' },
  'invite.created':        { verb: 'sent an invite to', kind: 'invite' },
  'user.deactivated':      { verb: 'deactivated', kind: 'status' },
  'org.deactivated':       { verb: 'deactivated', kind: 'status' },
  'user.reactivated':      { verb: 'reactivated', kind: 'status' },
  'org.reactivated':       { verb: 'reactivated', kind: 'status' },
  'org.deleted':           { verb: 'deleted', kind: 'status' },
};

export function buildHierarchyTree(payload: HierarchyPayload, now: Date = new Date()): HierarchyData {
  // One ClientRef per client_id, first row wins. The RPC guarantees this
  // (DISTINCT ON client_id), but the guard stays: client ids become React
  // list keys, and a duplicate key's failure mode is an orphaned stale row
  // in the DOM — the exact bug this dedupe exists to make impossible.
  const clientsByAgent = new Map<string, ClientRef[]>();
  const seenClients = new Set<string>();
  for (const c of payload.clients) {
    if (seenClients.has(c.client_id)) continue;
    seenClients.add(c.client_id);
    const list = clientsByAgent.get(c.agent_user_id) ?? [];
    list.push({ id: c.client_id, name: c.name, lastEdit: timeAgo(c.updated_at, now) });
    clientsByAgent.set(c.agent_user_id, list);
  }

  const nodes = new Map<string, OrgNode>();
  for (const o of payload.orgs) {
    nodes.set(o.id, {
      id: o.id,
      type: (['platform', 'imo', 'agency', 'team'].includes(o.org_type) ? o.org_type : 'imo') as OrgNode['type'],
      name: o.name,
      admin: o.owner_name ?? '',
      flags: { alts: true, fia: true }, // neutral — cascade lives in overrides
      children: [],
      clientCount: 0,
      clients: [],
      staffOwned: o.staff_owned === true,
    });
  }

  // Advisors become leaf nodes under their org: anyone with the agent role,
  // plus owners/admins who carry clients of their own ("IMO admins are also
  // advisors"). Owners with zero clients stay off the tree — they're already
  // the org's admin line.
  const personNode = (m: HierarchyPayload['members'][number], clients: ClientRef[]): OrgNode => ({
    id: `user-${m.user_id}`,
    type: 'agent',
    name: m.full_name || m.email || 'Unknown',
    admin: m.full_name || m.email || 'Unknown',
    email: m.email ?? undefined,
    flags: { alts: true, fia: true },
    children: [],
    clientCount: clients.length,
    clients,
    status: m.status === 'suspended' ? 'suspended' : m.status === 'pending' ? 'pending' : 'active',
    // Fail open: only an explicit false means "no book" (pre-patch payloads omit it).
    hasClientBook: m.has_client_book !== false,
    // Every member leaf renders as type 'agent'; this is what they actually
    // are, and it is what the Role control reads to show its current value.
    orgRole: m.org_role ?? undefined,
  });

  for (const m of payload.members) {
    const parent = nodes.get(m.org_id);
    if (!parent) continue;
    const clients = clientsByAgent.get(m.user_id) ?? [];
    // Every admin is listed on their org's node — the panel's Admins section
    // is where an owner suspends or removes one — whether or not they also
    // carry clients and so appear in the tree below.
    if (m.org_role === 'org_admin') {
      (parent.admins ??= []).push(personNode(m, clients));
    }
    const isAgentRole = m.org_role === 'agent';
    if (!isAgentRole && clients.length === 0) continue;
    parent.children.push(personNode(m, clients));
  }

  let root: OrgNode | null = null;
  for (const o of payload.orgs) {
    const node = nodes.get(o.id)!;
    if (o.id === payload.root_org_id) { root = node; continue; }
    const parent = o.parent_org_id ? nodes.get(o.parent_org_id) : undefined;
    if (parent) parent.children.push(node);
  }
  if (!root) throw new Error('Hierarchy payload missing root org');

  // Orgs sort before agent leaves at every level; alphabetical within kind.
  const sortChildren = (n: OrgNode) => {
    n.children.sort((a, b) =>
      (a.type === 'agent' ? 1 : 0) - (b.type === 'agent' ? 1 : 0) || a.name.localeCompare(b.name));
    n.children.forEach(sortChildren);
  };
  sortChildren(root);

  const overrides: Record<string, boolean> = {};
  for (const f of payload.flags) {
    if (f.flag_name === 'alts' || f.flag_name === 'fia') {
      overrides[`${f.org_id}__${f.flag_name}`] = f.enabled;
    }
  }
  // Per-agent overrides — agent node ids are `user-${user_id}`, so the key
  // lines up with the tree's node ids and hasFlag() needs no special case.
  for (const f of payload.user_flags ?? []) {
    if (f.flag_name === 'alts' || f.flag_name === 'fia') {
      overrides[`user-${f.user_id}__${f.flag_name}`] = f.enabled;
    }
  }

  const audit: AuditEntry[] = payload.audit.map(a => {
    const mapped = AUDIT_VERBS[a.action] ?? { verb: a.action, kind: 'add' as const };
    const d = (a.details ?? {}) as Record<string, unknown>;
    const target = String(d.scenario_name ?? d.client_name ?? d.email ?? d.name ?? d.flag ?? a.target_type ?? '');
    const detail = a.action === 'org.flag_changed'
      ? `${String(d.flag ?? '').toUpperCase()} ${d.reset ? 'reset to inherited' : d.enabled ? 'enabled' : 'disabled'}`
      : '';
    return {
      actor: a.actor_name ?? 'Someone',
      verb: mapped.verb,
      target,
      detail,
      time: timeAgo(a.created_at, now),
      kind: mapped.kind,
    };
  });

  return {
    root,
    isPlatformView: payload.is_platform_view,
    granted: { alts: payload.viewer_granted.alts, fia: payload.viewer_granted.fia },
    overrides,
    audit,
  };
}

// ---------------------------------------------------------------------------
// Hooks + RPC wrappers
// ---------------------------------------------------------------------------
export function useHierarchyTree() {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  // Impersonation is client-side (the admin's JWT never changes), so the RPC
  // must be told who we're viewing as or it anchors on the real super admin.
  const viewAs = isImpersonating ? impersonatedUserId : null;
  return useQuery({
    // CACHE KEY SCOPING (CLAUDE.md rule #9): must include the effective user id.
    queryKey: ['hierarchy-tree', viewAs ? `sandbox-${viewAs}` : `live-${user?.id}`],
    queryFn: async (): Promise<HierarchyData> => {
      const { data, error } = await supabase.rpc('get_hierarchy_tree', { p_view_as: viewAs });
      if (error) throw error;
      return buildHierarchyTree(data as unknown as HierarchyPayload);
    },
    enabled: !!user,
    staleTime: 30_000,
  });
}

/**
 * Persist a flag setting for any tree node. Org nodes carry their org UUID as
 * the node id; agent nodes are `user-${userId}` and route to the per-user RPC.
 */
export async function setNodeFlag(nodeId: string, flag: FlagKey, enabled: boolean): Promise<void> {
  if (nodeId.startsWith('user-')) {
    const { error } = await supabase.rpc('set_user_flag', {
      p_user: nodeId.slice(5), p_flag: flag, p_enabled: enabled,
    });
    if (error) throw error;
  } else {
    const { error } = await supabase.rpc('set_org_flag', {
      p_org: nodeId, p_flag: flag, p_enabled: enabled,
    });
    if (error) throw error;
  }
}

/** Removes the node's own flag setting so it follows its parent again
 *  (`clear_org_flag` / `clear_user_flag`, 20260924000001). Same authority as
 *  the setters: platform staff anywhere, or an owner/admin above the node. */
export async function clearNodeFlag(nodeId: string, flag: FlagKey): Promise<void> {
  const rpc = supabase.rpc as unknown as (
    fn: 'clear_org_flag' | 'clear_user_flag',
    args: Record<string, string>,
  ) => Promise<{ error: Error | null }>;
  const { error } = nodeId.startsWith('user-')
    ? await rpc('clear_user_flag', { p_user: nodeId.slice(5), p_flag: flag })
    : await rpc('clear_org_flag', { p_org: nodeId, p_flag: flag });
  if (error) throw error;
}

export async function fetchClientScenarios(clientId: string): Promise<TaxScenario[]> {
  const { data, error } = await supabase.rpc('get_client_scenarios', { p_client_id: clientId });
  if (error) throw error;
  return ((data as unknown[]) ?? []).map(rowToScenario);
}

// ---------------------------------------------------------------------------
// Gear-menu actions. Agent node ids are `user-${userId}`; org nodes carry the
// org UUID. Each throws on the server's permission/validation errors.
// ---------------------------------------------------------------------------
export async function setMemberStatus(userId: string, status: 'active' | 'suspended'): Promise<void> {
  const { error } = await supabase.rpc('set_member_status', { p_user: userId, p_status: status });
  if (error) throw error;
}

export async function renameOrg(orgId: string, name: string): Promise<void> {
  const { error } = await supabase.rpc('rename_org', { p_org: orgId, p_name: name });
  if (error) throw error;
}

export async function renameMember(userId: string, name: string): Promise<void> {
  const { error } = await supabase.rpc('rename_member', { p_user: userId, p_name: name });
  if (error) throw error;
}

/** Platform staff only (server-enforced): does this member carry a client book
 *  of their own? false hides My Clients and lands them on All Clients. */
export async function setMemberClientBook(userId: string, enabled: boolean): Promise<void> {
  const { error } = await (supabase.rpc as unknown as (
    fn: 'set_member_client_book',
    args: { p_user: string; p_enabled: boolean },
  ) => Promise<{ error: Error | null }>)('set_member_client_book', { p_user: userId, p_enabled: enabled });
  if (error) throw error;
}

/** Change a member's role inside your own downline (`set_member_role`,
 *  20260922000001). Server-enforced: platform staff anywhere, or an owner/admin
 *  above them; only an owner may grant org_admin. Refuses yourself, protected
 *  accounts and org owners. **Clients do not move** — a scenario is owned by
 *  `user_id`, so a promotion costs nobody their book. */
export async function setMemberRole(userId: string, role: 'agent' | 'org_admin'): Promise<void> {
  const { error } = await (supabase.rpc as unknown as (
    fn: 'set_member_role',
    args: { p_user: string; p_role: string },
  ) => Promise<{ error: Error | null }>)('set_member_role', { p_user: userId, p_role: role });
  if (error) throw error;
}

/** Remove a member from the hierarchy. Server-enforced: platform staff anywhere,
 *  or an owner/admin above the member (delete_member RPC, 20260918000002); refuses
 *  self, protected accounts and org owners. Their clients move to their org's
 *  owner. Goes through the admin-user-ops edge function because the login row
 *  needs the service role. Resolves to the number of scenarios moved. */
export async function deleteMember(userId: string): Promise<number> {
  const { data, error } = await supabase.functions.invoke('admin-user-ops', {
    body: { action: 'delete_member', user_id: userId },
  });
  if (error) {
    // A non-2xx response carries the real reason in its JSON body; the
    // FunctionsHttpError message itself is just "non-2xx status code".
    let msg = error.message;
    try {
      const body = await (error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
      if (body?.error) msg = body.error;
    } catch { /* keep the generic message */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(String(data.error));
  return Number(data?.scenarios_moved ?? 0);
}

type RpcWrite = (fn: string, args: Record<string, unknown>) => Promise<{ error: Error | null }>;

/** Move a person to another org in your downline, as agent or admin
 *  (`move_member`, 20260929000001). Their clients go with them. */
export async function moveMember(userId: string, destOrgId: string, role: 'agent' | 'org_admin'): Promise<void> {
  const { error } = await (supabase.rpc as unknown as RpcWrite)('move_member', { p_user: userId, p_dest: destOrgId, p_role: role });
  if (error) throw error;
}

/** Move an agency or team, with everyone under it, under another org
 *  (`move_org`, 20260929000001). */
export async function moveOrg(orgId: string, newParentId: string): Promise<void> {
  const { error } = await (supabase.rpc as unknown as RpcWrite)('move_org', { p_org: orgId, p_new_parent: newParentId });
  if (error) throw error;
}

export async function sendPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Downline invites (20260924000002). Every invite into an org at or below the
// viewer — who sent it, into where, and its state. The token never comes back:
// a manager can resend or revoke an invite someone under them sent, but only
// its creator ever sees the link.
// ---------------------------------------------------------------------------
export interface DownlineInvite {
  id: string;
  type: 'new_org' | 'org_member' | string;
  role: string | null;
  new_org_type: string | null;
  email: string | null;
  org_id: string;
  org_name: string;
  invited_by_name: string | null;
  redeemed_by_name: string | null;
  created_at: string;
  expires_at: string;
  redeemed_at: string | null;
}

/** What the invite is for, as the Invites panel names it. */
export function inviteKindLabel(inv: Pick<DownlineInvite, 'type' | 'role' | 'new_org_type'>): string {
  if (inv.type === 'new_org') {
    const t = inv.new_org_type;
    return t === 'imo' ? 'New IMO' : t === 'agency' ? 'New Agency' : t === 'team' ? 'New Team' : 'New organization';
  }
  return inv.role === 'org_admin' ? 'Admin' : 'Agent';
}

export function useDownlineInvites(enabled: boolean) {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  const viewAs = isImpersonating ? impersonatedUserId : null;
  return useQuery({
    // CACHE KEY SCOPING (CLAUDE.md rule #9): must include the effective user id.
    queryKey: ['downline-invites', viewAs ? `sandbox-${viewAs}` : `live-${user?.id}`],
    enabled,
    queryFn: async (): Promise<DownlineInvite[]> => {
      const { data, error } = await (supabase.rpc as unknown as (
        fn: 'get_downline_invites',
        args: { p_view_as: string | null },
      ) => Promise<{ data: unknown; error: Error | null }>)('get_downline_invites', { p_view_as: viewAs });
      if (error) throw error;
      return Array.isArray(data) ? (data as DownlineInvite[]) : [];
    },
  });
}

/** Restarts the invite's 7-day clock on the same link and emails it again.
 *  Returns whether an email actually went out (an open invite has no address). */
export async function resendDownlineInvite(inviteId: string): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke('send-invite-email', {
    body: { invite_id: inviteId, resend: true },
  });
  if (error) {
    let msg = error.message;
    try {
      const body = await (error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
      if (body?.error) msg = body.error;
    } catch { /* keep the transport message */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(String(data.error));
  return !!data?.emailSent;
}

export async function revokeDownlineInvite(inviteId: string): Promise<void> {
  const { error } = await (supabase.rpc as unknown as (
    fn: 'revoke_downline_invite',
    args: { p_invite: string },
  ) => Promise<{ error: Error | null }>)('revoke_downline_invite', { p_invite: inviteId });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// One member's Alts / FIA state for the Team page's member panel
// (get_member_strategy_flags, 20260925000001). `set` is the person's own
// setting, null when they follow their organization; `org` is what the
// organization's cascade says.
// ---------------------------------------------------------------------------
export interface MemberStrategyFlags {
  alts: { set: boolean | null; org: boolean };
  fia: { set: boolean | null; org: boolean };
}

export async function fetchMemberStrategyFlags(userId: string): Promise<MemberStrategyFlags> {
  const { data, error } = await (supabase.rpc as unknown as (
    fn: 'get_member_strategy_flags',
    args: { p_user: string },
  ) => Promise<{ data: unknown; error: Error | null }>)('get_member_strategy_flags', { p_user: userId });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, boolean | null | undefined>;
  const set = (v: boolean | null | undefined) => (typeof v === 'boolean' ? v : null);
  // A missing org value reads as on: nothing set anywhere means on (org_flag_state's default).
  return {
    alts: { set: set(d.alts_set), org: d.alts_org !== false },
    fia: { set: set(d.fia_set), org: d.fia_org !== false },
  };
}
